import { FormEvent, useEffect, useMemo, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import {
  getProjectProvisionStatus,
  previewProjectMove,
  startProjectMove,
} from '@frontend/api-clients/adminProjectProvisionClient';
import type { EcosystemAdminEntry } from '@frontend/types';
import { useProvisionPoll } from '../../../hooks/useProvisionPoll';
import ProvisionRunView from './ProvisionRunView';

export interface MoveProjectPanelProject {
  id: string;
  name: string;
  ecosystemId: string;
}

export interface MoveProjectPanelProps {
  project: MoveProjectPanelProject;
  ecosystems: EcosystemAdminEntry[];
  onCancel: () => void;
  onSuccess: () => void;
}

type WizardPhase = 'select' | 'impact' | 'running';

export default function MoveProjectPanel({
  project,
  ecosystems,
  onCancel,
  onSuccess,
}: MoveProjectPanelProps) {
  const [phase, setPhase] = useState<WizardPhase>('select');
  const [targetEcosystemId, setTargetEcosystemId] = useState('');
  const [impact, setImpact] = useState<{
    plannedSteps: string[];
    source: string;
    target: string;
    cascadedProjectIds: string[];
  } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    setPhase('select');
    setTargetEcosystemId('');
    setImpact(null);
    setError('');
    setCorrelationId(null);
    setToken(null);
  }, [project.id]);

  const targetOptions = useMemo(
    () =>
      ecosystems.filter((e) => !e.decommissioned && e.id !== project.ecosystemId && e.memberGroup),
    [ecosystems, project.ecosystemId]
  );

  const { status, error: pollError, polling } = useProvisionPoll({
    token,
    correlationId,
    fetchStatus: getProjectProvisionStatus,
    enabled: phase === 'running' && Boolean(correlationId),
    onTerminal: (s) => {
      if (s.runState === 'completed') onSuccess();
    },
  });

  const onPreviewImpact = async (event: FormEvent) => {
    event.preventDefault();
    if (!targetEcosystemId) {
      setError('Select a target ecosystem.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const t = authClient.getToken();
      if (!t) throw new Error('You must be signed in.');
      const preview = await previewProjectMove(t, project.id, targetEcosystemId);
      setImpact({
        plannedSteps: preview.plannedSteps,
        source: preview.sourceEcosystemId,
        target: preview.targetEcosystemId,
        cascadedProjectIds: preview.cascadedProjectIds || [],
      });
      setPhase('impact');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impact preview failed');
    } finally {
      setBusy(false);
    }
  };

  const onMove = async () => {
    setBusy(true);
    setError('');
    try {
      const t = authClient.getToken();
      if (!t) throw new Error('You must be signed in.');
      const start = await startProjectMove(t, project.id, targetEcosystemId);
      setToken(t);
      setCorrelationId(start.correlationId);
      setPhase('running');
    } catch (e) {
      const err = e as Error & { correlationId?: string };
      if (err.correlationId) setCorrelationId(err.correlationId);
      setError(err.message || 'Failed to start move');
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'running') {
    return (
      <div className="admin-orch-wizard">
        <ProvisionRunView
          title="Project move status"
          status={status}
          pollError={pollError}
          loading={polling && !status}
        />
        {status &&
        (status.runState === 'completed' ||
          status.runState === 'failed' ||
          status.runState === 'partial') ? (
          <div className="admin-create-user-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Close
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  if (phase === 'impact') {
    return (
      <div className="admin-orch-wizard">
        <h4 className="admin-orch-subtitle">Impact summary</h4>
        <p className="ecosystem-description">
          Moving <strong>{project.name}</strong> (
          <code className="admin-inline-code">{project.id}</code>) from{' '}
          <code className="admin-inline-code">{impact?.source}</code> to{' '}
          <code className="admin-inline-code">{impact?.target}</code>. Sub-projects move with this
          top-level project.
        </p>
        {(impact?.cascadedProjectIds?.length || 0) > 0 ? (
          <p className="ecosystem-description">
            Cascading {(impact?.cascadedProjectIds || []).length} sub-project
            {(impact?.cascadedProjectIds || []).length === 1 ? '' : 's'}.
          </p>
        ) : null}
        <ul className="admin-sync-status-list">
          {(impact?.plannedSteps || []).map((step) => (
            <li key={step}>
              <span className="admin-sync-status-label">{step.replace(/_/g, ' ')}</span>
            </li>
          ))}
        </ul>
        {error ? (
          <div className="settings-message settings-error" role="alert">
            {error}
          </div>
        ) : null}
        <div className="admin-create-user-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void onMove()}>
            {busy ? 'Starting…' : 'Move project'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => setPhase('select')}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="admin-orch-wizard" onSubmit={onPreviewImpact}>
      <h4 className="admin-orch-subtitle">Move project</h4>
      <p className="ecosystem-description">
        Move <strong>{project.name}</strong> (
        <code className="admin-inline-code">{project.id}</code>) and its sub-projects to another
        ecosystem. Portal policy updates only after downstream steps succeed.
      </p>
      <label className="admin-orch-field" htmlFor="eco-orch-target-eco">
        <span>Target ecosystem</span>
        <select
          id="eco-orch-target-eco"
          value={targetEcosystemId}
          onChange={(e) => setTargetEcosystemId(e.target.value)}
          required
        >
          <option value="">Select ecosystem…</option>
          {targetOptions.map((e) => (
            <option key={e.id} value={e.id}>
              {e.displayName} ({e.id})
            </option>
          ))}
        </select>
      </label>
      {error ? (
        <div className="settings-message settings-error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="admin-create-user-actions">
        <button type="submit" className="btn btn-primary" disabled={busy || !targetEcosystemId}>
          {busy ? 'Loading…' : 'Preview impact'}
        </button>
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
